import { useEffect, useState } from 'react';
import axios from 'axios';
import { PaperPlaneTilt, UploadSimple, ArrowCounterClockwise, CheckCircle, Warning } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const API = '/api/eccairs';
const EMPTY = {
  responsible_entity: '', organisation_code: '', state_of_occurrence: '', originating_authority: '', narrative_language: 'English',
  e2_enabled: false, e2_base_url: '', e2_token_path: '', e2_import_path: '', e2_client_id: '', e2_client_secret: '', e2_username: '', e2_password: '',
};

export default function EccairsSettingsPanel() {
  const [form, setForm] = useState(EMPTY);
  const [secretsSet, setSecretsSet] = useState({});
  const [taxonomy, setTaxonomy] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, t] = await Promise.all([
      axios.get(`${API}/settings`, { withCredentials: true }),
      axios.get(`${API}/taxonomy`, { withCredentials: true }),
    ]);
    const data = s.data || {};
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]])), e2_client_secret: '', e2_password: '' });
    setSecretsSet({ e2_client_secret: data.e2_client_secret_set, e2_password: data.e2_password_set });
    setTaxonomy(t.data);
  };

  useEffect(() => { load().catch(() => setMessage({ ok: false, text: 'Could not load ECCAIRS settings' })); }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const run = async (fn, okText) => {
    setBusy(true); setMessage(null);
    try { const res = await fn(); setMessage({ ok: true, text: res?.data?.message || okText }); await load(); }
    catch (err) { setMessage({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const save = () => run(() => axios.put(`${API}/settings`, form, { withCredentials: true }), 'Saved');
  const testE2 = () => run(() => axios.post(`${API}/settings/test-e2`, {}, { withCredentials: true }), 'Connected');
  const importPack = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    e.target.value = '';
    run(() => axios.post(`${API}/taxonomy/import`, body, { withCredentials: true }), 'Installed');
  };
  const resetPack = () => window.confirm('Go back to the built-in starter taxonomy?') &&
    run(() => axios.post(`${API}/taxonomy/reset`, {}, { withCredentials: true }), 'Reverted');

  const field = (key, label, props = {}) => (
    <div>
      <Label htmlFor={`ec-${key}`}>{label}</Label>
      <Input id={`ec-${key}`} value={form[key]} onChange={set(key)} className="rounded-lg mt-1.5" {...props} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="eccairs-settings">
      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm border ${message.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>{message.text}</div>
      )}

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="font-semibold text-slate-900 mb-1">Reporting authority</h2>
        <p className="text-sm text-slate-500 mb-5">Used to prefill ADREP records and on every report RootTrace generates.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {field('responsible_entity', 'Responsible entity (ECCAIRS #453)', { placeholder: 'e.g. Air Accident Investigation Bureau' })}
          {field('organisation_code', 'Organisation code for E5X file names', { placeholder: 'Up to 12 letters/digits' })}
          {field('state_of_occurrence', 'Default State / area of occurrence', { placeholder: 'Your State' })}
          {field('narrative_language', 'Default narrative language')}
          <div className="md:col-span-2">{field('originating_authority', 'Originating authority and contact (Annex 13, 4.2 l)')}</div>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="font-semibold text-slate-900 mb-1">ECCAIRS taxonomy</h2>
        {taxonomy && (
          <div className={`rounded-lg border p-4 text-sm my-4 ${taxonomy.active.official ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
            <div className="flex items-center gap-2 font-medium">
              {taxonomy.active.official ? <CheckCircle size={16} /> : <Warning size={16} />}
              {taxonomy.active.name}{taxonomy.active.taxonomy_version ? ` (version ${taxonomy.active.taxonomy_version})` : ''}
            </div>
            {!taxonomy.active.official && (
              <p className="mt-1">Lists awaiting official codes: {taxonomy.active.pending_lists.join(', ').replaceAll('_', ' ')}.
                Import the official pack for your ECCAIRS 2 environment to complete E5X coding.</p>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">
            <UploadSimple size={16} />Import taxonomy pack (JSON)
            <input type="file" accept="application/json,.json" className="hidden" onChange={importPack} data-testid="taxonomy-import" />
          </label>
          {taxonomy?.active.official && (
            <Button variant="outline" className="rounded-lg" onClick={resetPack} disabled={busy}><ArrowCounterClockwise size={16} className="mr-2" />Use starter pack</Button>
          )}
        </div>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="font-semibold text-slate-900">ECCAIRS 2 API connection (optional)</h2>
            <p className="text-sm text-slate-500 mt-1">Only for States whose ECCAIRS 2 environment accepts automated transfer. Requires API credentials from your E2 administrator and this server's IP address whitelisted by EASA. Test in the EASA sandbox first.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 shrink-0">
            <Switch checked={form.e2_enabled} onCheckedChange={(v) => setForm({ ...form, e2_enabled: v })} aria-label="Enable ECCAIRS 2 API" className="data-[state=unchecked]:bg-slate-300" />
            {form.e2_enabled ? 'On' : 'Off'}
          </label>
        </div>
        {form.e2_enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {field('e2_base_url', 'API base URL', { placeholder: 'https://api.aviationreporting.eu' })}
            {field('e2_token_path', 'Token path', { placeholder: '/auth/api/token' })}
            {field('e2_import_path', 'E5X import path (from the EASA E2 API guide)', { placeholder: '/...' })}
            {field('e2_client_id', 'Client id')}
            {field('e2_client_secret', 'Client secret', { type: 'password', placeholder: secretsSet.e2_client_secret ? 'Saved - leave blank to keep' : '' })}
            {field('e2_username', 'API user name')}
            {field('e2_password', 'API user password', { type: 'password', placeholder: secretsSet.e2_password ? 'Saved - leave blank to keep' : '' })}
          </div>
        )}
      </section>

      <div className="flex gap-3">
        <Button onClick={save} disabled={busy} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="eccairs-save">Save ECCAIRS settings</Button>
        {form.e2_enabled && <Button variant="outline" onClick={testE2} disabled={busy} className="rounded-lg"><PaperPlaneTilt size={16} className="mr-2" />Test connection</Button>}
      </div>
    </div>
  );
}
